import { memo, useEffect } from 'react';
import { Button, BUTTON_THEMES, Icon, Grid } from 'ht-styleguide';
import { homePath } from '@constants/paths';
import { isDevelopment } from '@utils/environment';
import Chat from '@integrations/gladly/Chat/Gladly.methods';
import { Logger } from '@utils/logger';

import styles from './defaulterrorpage.module.scss';

type DefaultErrorPageProps = {
  error?: Error;
  statusCode?: number;
  page?: string;
  standalone?: boolean;
};

const AppLevelFallback = ({ error, statusCode, page, standalone = false }: DefaultErrorPageProps) => {
  const goHome = () => {
    window.location.href = homePath();
  };

  const openChat = () => {
    Chat.open();
  };

  let errorString;
  if (error instanceof Error) {
    errorString = JSON.stringify(error, Object.getOwnPropertyNames(error));
  } else {
    errorString = JSON.stringify(error);
  }

  useEffect(() => {
    if (standalone) {
      Logger('Default Error Page')(`Stats: ${statusCode} page: ${page} - ${error}`);
    }
  }, [error, page, standalone, statusCode]);

  return (
    <div className={`site-v2 ${styles.mainContainer}`}>
      <header className={styles.header}>
        <nav>
          <Grid.Fluid>
            <Grid.Row>
              <Grid.Column>
                <a href={homePath()}>
                  <Icon name="ht-logo" className={styles.logo} />
                </a>
              </Grid.Column>
            </Grid.Row>
          </Grid.Fluid>
        </nav>
      </header>
      <section className={`paddingY-huge1 ${styles.section}`}>
        {!isDevelopment() ? (
          <Grid.Fluid>
            <Grid.Row>
              <Grid.Column sm={4} mdOffset={1} md={6} lgOffset={4} lg={4} classes={styles.column}>
                <h2>Looks like this page is unavailable</h2>
                <p className="p1 marginTop-small">
                  Don&apos;t worry, our highly-trained technicians are still available to assist you with your tech needs.
                </p>
                <div className={styles.buttonContainer}>
                  <Button onClick={goHome} theme={BUTTON_THEMES.INVERTED} className={`marginTop-medium2 ${styles.button}`}>
                    Book a Service
                  </Button>
                  <Button onClick={openChat} theme={BUTTON_THEMES.INVERTED} className={`marginTop-small1 ${styles.button}`}>
                    Chat with Us
                  </Button>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid.Fluid>
        ) : (
          <Grid.Fluid>
            <Grid.Row>
              <Grid.Column sm={4} mdOffset={1} md={6} lgOffset={2} lg={8} classes={styles.column}>
                <h2>Something borked up</h2>
                <p className="marginY-small">It&apos;s cool. You got this. &#128170;</p>
                <p>
                  <code>{errorString}</code>
                </p>
              </Grid.Column>
            </Grid.Row>
          </Grid.Fluid>
        )}
      </section>
    </div>
  );
};

export default memo(AppLevelFallback);
